import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Calendar,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { QuestionnaireSubmission } from "@/domain/intake";

interface LeadContext {
  leadId: string;
  email: string;
  name: string;
  packageId: string | null;
  emailVerified?: boolean;
  engagementPath?: string;
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getQuestionnaireData(): QuestionnaireSubmission | null {
  try {
    const raw = localStorage.getItem("echopeak-questionnaire-data");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function QuestionnaireSubmittedPage() {
  const navigate = useNavigate();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [questionnaireData, setQuestionnaireData] =
    useState<QuestionnaireSubmission | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx || !ctx.emailVerified) {
      navigate("/verify/email");
      return;
    }
    setLeadContext(ctx);
    setQuestionnaireData(getQuestionnaireData());
  }, [navigate]);

  if (!leadContext) return null;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-32 pb-24">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full relative z-10"
      >
        <Card className="glass-card border-gradient text-center">
          <CardContent className="pt-12 pb-8 px-8">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold mb-4">
              We've received your project details
            </h1>

            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Our team will review your responses within 1-2 business days and
              reach out to{" "}
              <span className="font-medium text-foreground">
                {leadContext.email}
              </span>{" "}
              with next steps.
            </p>

            <div className="text-left bg-card/50 rounded-lg p-6 mb-8">
              <h3 className="font-semibold mb-4">What happens next</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-3">
                  <span className="text-primary font-semibold shrink-0">
                    1-2 days:
                  </span>
                  Our team reviews your responses and prepares a scope
                  recommendation
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-semibold shrink-0">
                    Next:
                  </span>
                  We'll email you with our assessment, recommended approach, and
                  pricing
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-semibold shrink-0">
                    Then:
                  </span>
                  If you'd like to proceed, we'll send contract documents for
                  review and signing
                </li>
              </ul>
            </div>

            {questionnaireData && (
              <div className="text-left mb-8">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors mx-auto"
                >
                  {showDetails ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  {showDetails
                    ? "Hide submitted answers"
                    : "View submitted answers"}
                </button>

                {showDetails && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 p-4 rounded-lg bg-muted/50 border text-sm space-y-3"
                  >
                    <div>
                      <p className="font-medium">Problem to solve</p>
                      <p className="text-muted-foreground">
                        {questionnaireData.common.problemToSolve || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Success criteria</p>
                      <p className="text-muted-foreground">
                        {questionnaireData.common.successCriteria || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Timeline</p>
                      <p className="text-muted-foreground">
                        {questionnaireData.common.timeline || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">Decision makers</p>
                      <p className="text-muted-foreground">
                        {questionnaireData.common.decisionMakers || "—"}
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            <Card className="bg-primary/5 border-primary/20 mb-6">
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-primary shrink-0" />
                  <div className="text-left">
                    <p className="text-sm font-medium">
                      Want to discuss your responses?
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Schedule a brief 15-30 minute follow-up call instead
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 ml-auto"
                    onClick={() => {
                      if (leadContext) {
                        localStorage.setItem(
                          "echopeak-lead-context",
                          JSON.stringify({
                            ...leadContext,
                            engagementPath: "call",
                          }),
                        );
                      }
                      navigate("/schedule");
                    }}
                  >
                    Schedule
                    <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (leadContext) {
                    localStorage.setItem(
                      "echopeak-lead-context",
                      JSON.stringify({
                        ...leadContext,
                        engagementPath: "call",
                      }),
                    );
                  }
                  navigate("/schedule");
                }}
              >
                Switch to full discovery call
              </Button>
              <Button onClick={() => navigate("/")}>Return to Home</Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
